import axios from './axios';
import auth from '../resources/Auth';

export default {
  /**
   * Get all prescription requests for the current user
   * @param {Object} filters - Optional filters (status, type)
   * @returns {Promise}
   */
  getAll: async (filters = {}) => {
    let url = `${process.env.REACT_APP_API_URL}/prescription-requests`;

    // Add query parameters if filters provided
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return await axios.get(url, {
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  },

  /**
   * Get a single prescription request by ID
   * @param {number} id - Prescription request ID
   * @returns {Promise}
   */
  get: async (id) => {
    let url = `${process.env.REACT_APP_API_URL}/prescription-requests/${id}`;

    return await axios.get(url, {
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  },

  /**
   * Create a new prescription request
   * @param {Object} prescriptionRequest - The prescription request data
   * @returns {Promise}
   */
  create: async (prescriptionRequest) => {
    let url = `${process.env.REACT_APP_API_URL}/prescription-requests`;

    // Stringify file if present
    if (prescriptionRequest.file && typeof prescriptionRequest.file !== 'string') {
      prescriptionRequest.file = JSON.stringify(prescriptionRequest.file);
    }

    return await axios.post(url, prescriptionRequest, {
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  },

  /**
   * Update a prescription request (for pharmacy updates)
   * @param {number} id - Prescription request ID
   * @param {Object} updates - Fields to update (status, pharmacy_note)
   * @returns {Promise}
   */
  update: async (id, updates) => {
    let url = `${process.env.REACT_APP_API_URL}/prescription-requests/${id}`;

    return await axios.put(url, updates, {
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  },

  /**
   * Delete a prescription request (only if pending)
   * @param {number} id - Prescription request ID
   * @returns {Promise}
   */
  remove: async (id) => {
    let url = `${process.env.REACT_APP_API_URL}/prescription-requests/${id}`;

    return await axios.delete(url, {
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  },

  /**
   * Download the file associated with a prescription request
   * @param {number} id - Prescription request ID
   * @returns {Promise<Blob>} The file blob
   */
  downloadFile: async (id) => {
    let url = `${process.env.REACT_APP_API_URL}/prescription-requests/${id}/file`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${auth.token}` },
      responseType: 'blob',
    });

    return response.data;
  },

  /**
   * Get status options for dropdowns
   * @returns {Array} Array of status objects with label and value
   */
  getStatusOptions: () => {
    return [
      { value: 'pending', label: 'In attesa' },
      { value: 'in_elaboration', label: 'In elaborazione' },
      { value: 'confirmed', label: 'Confermata' },
      { value: 'delivered', label: 'Consegnata' },
      { value: 'cancelled', label: 'Annullata' },
    ];
  },

  /**
   * Get type options for dropdowns
   * @returns {Array} Array of type objects with label and value
   */
  getTypeOptions: () => {
    return [
      { value: 'fse', label: 'Fascicolo Sanitario' },
      { value: 'nre', label: 'Numero Ricetta (NRE)' },
      { value: 'file', label: 'File caricato' },
    ];
  },

  /**
   * Get human readable status label
   * @param {string} status - Status value
   * @returns {string} Human readable label
   */
  getStatusLabel: (status) => {
    const options = {
      pending: 'In attesa',
      in_elaboration: 'In elaborazione',
      confirmed: 'Confermata',
      delivered: 'Consegnata',
      cancelled: 'Annullata',
    };
    return options[status] || status;
  },

  /**
   * Get human readable type label
   * @param {string} type - Type value
   * @returns {string} Human readable label
   */
  getTypeLabel: (type) => {
    const options = {
      fse: 'Fascicolo Sanitario',
      nre: 'Numero Ricetta (NRE)',
      file: 'File caricato',
    };
    return options[type] || type;
  },
};
